import { logger } from '@adornis/base/logging.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ownMaxPoints } from '../../../../../../db/utils/points-model.js';
import type { GradingScale } from '../../../../../../shared/grading-scales.js';
import {
  averageNumericGrade,
  gradeFromPercentage,
  parseGradingScale,
} from '../../../../../../shared/grading-scales.js';
import type { GrammarFiSettings } from '../../../../../../shared/grammar-grading.js';
import {
  GRAMMAR_TASK_TYPES,
  applyGradeStepDeduction,
  fiDeductionSteps,
  parseGrammarFiSettings,
} from '../../../../../../shared/grammar-grading.js';
import type { TaskGrammarMode } from '../../../../../../shared/grammar-rules-resolution.js';
import {
  findLanguageUseRuleResultsForTask,
  formatTaskReferenceForDisplay,
  getSoloLanguageUseRule,
  resolveAlwaysUseFi,
  resolveLanguageUseGroupPoints,
  resolveSoloLanguageUsePoints,
  taskGrammarMode,
} from '../../../../../../shared/grammar-rules-resolution.js';
import { GRADING_THRESHOLDS } from '../../../../../lib/constants.js';
import { useCurrentUserProfile } from '../../../../../lib/useCurrentUserProfile.js';
import { findLanguageUseUncertainties, findUncertainties, flattenSubTasksWithMeta } from '../../grading/utils.js';
import type {
  ClarificationCase,
  ClarificationSortDirection,
  ClarificationSortField,
  CorrectionSummaryProps,
  LanguageUseGroupColumn,
  StudentSummary,
  SummaryTabKey,
  TaskDefinition,
  TaskStatusSummary,
} from '../utils.js';
import {
  getGradeCardClasses,
  getPointsCardClasses,
  isTaskFinishedByState,
  normalizeExplanation,
  parseNumber,
  resolveAchievedPoints,
  resolveMaxPoints,
  resolveSoloLanguageUseMaxPointsForTask,
  resolveSummaryUnresolvedConfidence,
  roundToOne,
  taskHasSoloLanguageUseCriteria,
  unwrapDoc,
} from '../utils.js';
import { useSummaryDom } from './use-summary-dom.js';

export function useSummaryState(props: CorrectionSummaryProps) {
  const {
    submissions,
    exam,
    onSaveUpdate,
    onDownloadReports,
    isDownloadingReports,
    canDownloadReports,
    onDownloadGradebookCsv,
    isDownloadingGradebookCsv,
    canDownloadGradebookCsv,
    onNavigateToTask,
  } = props;

  const dom = useSummaryDom();
  const { role } = useCurrentUserProfile();

  // --- STATE ---
  const [activeTab, setActiveTabRaw] = useState<SummaryTabKey>('students');
  const [studentQuery, setStudentQuery] = useState('');
  const [clarificationDecisions, setClarificationDecisions] = useState<Record<string, 'approve' | 'reject'>>({});
  const [clarificationSaving, setClarificationSaving] = useState<Record<string, boolean>>({});
  const [isCurveEnabled, setIsCurveEnabled] = useState(false);
  const [curveStrength, setCurveStrength] = useState(0.2);
  const [gradingScale, setGradingScale] = useState<GradingScale>(() =>
    parseGradingScale((exam as any)?.gradingScaleJson ?? null),
  );
  const pendingScaleRef = useRef<GradingScale | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync state when the exam's saved scale changes (canvas reopened with fresh exam data)
  useEffect(() => {
    if (pendingScaleRef.current) return; // user has unsaved changes in flight
    setGradingScale(parseGradingScale((exam as any)?.gradingScaleJson ?? null));
  }, [(exam as any)?.gradingScaleJson]);

  // Grammar grading: resolved mode (FI vs. rules) + per-exam FI deduction settings, same
  // debounce/flush persistence pattern as the grading scale above.
  const [grammarFiSettings, setGrammarFiSettings] = useState<GrammarFiSettings>(() =>
    parseGrammarFiSettings((exam as any)?.grammarFiSettingsJson ?? null),
  );
  const pendingFiSettingsRef = useRef<GrammarFiSettings | null>(null);
  const fiDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (pendingFiSettingsRef.current) return; // user has unsaved changes in flight
    setGrammarFiSettings(parseGrammarFiSettings((exam as any)?.grammarFiSettingsJson ?? null));
  }, [(exam as any)?.grammarFiSettingsJson]);
  const [sortField, setSortField] = useState<ClarificationSortField>('uncertainty');
  const [sortDirection, setSortDirection] = useState<ClarificationSortDirection>('desc');

  // Flush any pending debounced scale save immediately
  const flushPendingScale = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (pendingScaleRef.current && props.onSaveGradingScale) {
      props
        .onSaveGradingScale(pendingScaleRef.current)
        .catch(err => logger.error({ err, context: 'flushPendingScale' }, 'Failed to save grading scale'));
      pendingScaleRef.current = null;
    }
  };

  const flushPendingFiSettings = () => {
    if (fiDebounceTimerRef.current) clearTimeout(fiDebounceTimerRef.current);
    if (pendingFiSettingsRef.current && props.onSaveGrammarFiSettings) {
      props
        .onSaveGrammarFiSettings(pendingFiSettingsRef.current)
        .catch(err => logger.error({ err, context: 'flushPendingFiSettings' }, 'Failed to save grammar FI settings'));
      pendingFiSettingsRef.current = null;
    }
  };

  const setActiveTab = (tab: SummaryTabKey) => {
    if (activeTab === 'curving' && tab !== 'curving') flushPendingScale();
    if (activeTab === 'languageUse' && tab !== 'languageUse') flushPendingFiSettings();
    setActiveTabRaw(tab);
  };

  // --- COMPUTED / AGGREGATIONS ---
  const languageUseSchemeRules: any[] = Array.isArray((exam as any)?.languageUseSchemeRules)
    ? (exam as any).languageUseSchemeRules
    : [];
  const taskDefinitions: TaskDefinition[] =
    exam?.tasks?.map((task: any, index: number) => {
      const taskNumber = String(task?.taskNumber ?? index + 1);
      return {
        taskKey: taskNumber,
        label: taskNumber.toLowerCase().includes('aufgabe') ? taskNumber : `Aufgabe ${taskNumber}`,
        maxPoints: resolveMaxPoints(task),
        languageUseMaxPoints: resolveSoloLanguageUseMaxPointsForTask(task, languageUseSchemeRules),
        hasOwnLanguageUseCriteria: taskHasSoloLanguageUseCriteria(task, languageUseSchemeRules),
      };
    }) ?? [];

  const fallbackTaskDefinitions: TaskDefinition[] =
    taskDefinitions.length > 0
      ? taskDefinitions
      : Array.from(
          new Set(submissions.flatMap(s => (s.taskSubmissions || []).map((t, i) => String(t.taskNumber ?? i + 1)))),
        ).map(taskNumber => ({
          taskKey: taskNumber,
          label: taskNumber.toLowerCase().includes('aufgabe') ? taskNumber : `Aufgabe ${taskNumber}`,
          // No exam task to derive a max from (submissions-only fallback) → unknown max = 0, not a silent 1.
          maxPoints: 0,
          languageUseMaxPoints: 0,
          hasOwnLanguageUseCriteria: false,
        }));

  const activeTaskDefinitions = fallbackTaskDefinitions;

  // Part C2: one column per distinct multi-task LanguageUseScheme group, built directly from
  // exam.languageUseSchemeRules (independent of the per-task tree walk), ordered by each group's
  // lowest linked task number for predictable teacher-facing ordering. Solo (1-member) rules are
  // excluded — those are already counted per-task above (languageUseMaxPoints/hasOwnLanguageUseCriteria).
  const multiMemberLanguageUseSchemeRules = languageUseSchemeRules.filter(
    (scheme: any) => Array.isArray(scheme?.dependsOn) && scheme.dependsOn.length > 1,
  );
  const lowestDependsOnTaskNumber = (dependsOn: string[]): number => {
    // dependsOn entries are "|"-joined qualified paths (e.g. "2|a") — sort by the top-level task
    // number (the first segment), not the whole string.
    const numeric = dependsOn.map(tn => Number(tn.split('|')[0])).filter(n => Number.isFinite(n));
    return numeric.length > 0 ? Math.min(...numeric) : Number.POSITIVE_INFINITY;
  };
  const languageUseGroupColumns: LanguageUseGroupColumn[] = multiMemberLanguageUseSchemeRules
    .map((scheme: any) => {
      const dependsOn = Array.isArray(scheme?.dependsOn) ? scheme.dependsOn.map(String) : [];
      return {
        schemeId: String(scheme?._id ?? ''),
        label: `Gruppe: Aufgabe ${dependsOn.map(formatTaskReferenceForDisplay).join(', ')}`,
        maxPoints: Math.max(0, parseNumber(scheme?.maxPoints) ?? 0),
        dependsOn,
      };
    })
    .sort((a, b) => lowestDependsOnTaskNumber(a.dependsOn) - lowestDependsOnTaskNumber(b.dependsOn));
  const languageUseGroupsMaxPoints = languageUseGroupColumns.reduce((sum, col) => sum + col.maxPoints, 0);

  // Part C3: grand total routes through the same resolveLanguageUseGroupPoints primitive the server
  // uses (generate-grading-reports.ts) — own-criteria language-use is its own addend (shown in a
  // separate "· Sprachliche Leistung" column, not folded into the task's own points), and group points
  // are added once per group, never per member task.
  const totalPossiblePoints =
    activeTaskDefinitions.reduce((sum, task) => sum + task.maxPoints + task.languageUseMaxPoints, 0) +
    languageUseGroupsMaxPoints;

  // Part C4: per-task grammar mode (none/fi/languageUse), computed once for the whole exam tree so
  // the FI matrix tab and the legacy FI summary column can be rescoped to only still-FI tasks in
  // mixed-mode exams (some tasks graded via rules, others still via Fehlerindex).
  // Deliberately reads grammarFiSettings.alwaysUseFi (this hook's own optimistic local mirror of
  // Exam.grammarFiSettingsJson, updated instantly on switch click) rather than the props.examAlwaysUseFi/
  // grammarGradingMode prop chat.tsx derives straight from the live exam subscription — that prop only
  // updates after the mutation round-trips back down, which is exactly the delay a teacher would feel
  // when flipping the FI switch or watching this panel react to it.
  const forceFi = resolveAlwaysUseFi(grammarFiSettings.alwaysUseFi, props.teacherAlwaysUseFi);
  // Keyed by the same "|"-joined qualified path LanguageUseScheme.dependsOn now stores (see
  // build-exam-tasks.ts's collectAllTaskNumbers) — a bare local taskNumber like "a" is ambiguous
  // whenever it's reused as a subtask label under more than one parent.
  const taskGrammarModeByTaskKey: Record<string, TaskGrammarMode> = {};
  const collectGrammarModes = (tasks: any[] | null | undefined, parentPath: string[] = []) => {
    for (const task of tasks ?? []) {
      const taskNumber = String(task?.taskNumber ?? '');
      const path = taskNumber ? [...parentPath, taskNumber] : parentPath;
      if (taskNumber) {
        const qualified = path.join('|');
        taskGrammarModeByTaskKey[qualified] = taskGrammarMode(task, qualified, languageUseSchemeRules);
      }
      collectGrammarModes(task?.subTasks, path);
    }
  };
  collectGrammarModes(exam?.tasks as any[] | null | undefined);

  // Whether ANY task needs grammar evaluation at all (mode 'fi' or 'languageUse'), regardless of which
  // mode currently applies — drives whether the Language Use tab renders its override/settings UI at
  // all, as opposed to grammarTaskDefinitions below, which only lists still-FI tasks for the error matrix.
  const hasAnyGrammarEligibleTask = Object.values(taskGrammarModeByTaskKey).some(mode => mode !== 'none');

  const resolveStudentName = (studentName: unknown, fallbackLabel: string): string => {
    if (typeof studentName === 'string' && studentName.trim().length > 0) return studentName.trim();
    return fallbackLabel;
  };

  const studentSummaries: StudentSummary[] = useMemo(
    () =>
      submissions.map((sub, idx) => {
        const pointsByTask: Record<string, number> = {};
        const ownLanguageUsePointsByTask: Record<string, number> = {};
        const ownLanguageUseConfidenceByTask: Record<string, number | null> = {};
        const ownLanguageUseIsApprovedByTask: Record<string, boolean> = {};
        const gradingByTask: Record<string, string> = {};
        const confidenceByTask: Record<string, number | null> = {};
        const isApprovedByTask: Record<string, boolean> = {};

        for (const taskDef of activeTaskDefinitions) {
          const mainTask =
            (sub.taskSubmissions || []).find(t => String(t.taskNumber ?? '') === taskDef.taskKey) ??
            (sub.taskSubmissions || [])[Number(taskDef.taskKey) - 1];
          if (!mainTask) {
            pointsByTask[taskDef.taskKey] = 0;
            ownLanguageUsePointsByTask[taskDef.taskKey] = 0;
            ownLanguageUseConfidenceByTask[taskDef.taskKey] = null;
            ownLanguageUseIsApprovedByTask[taskDef.taskKey] = false;
            gradingByTask[taskDef.taskKey] = 'Noch kein Grading vorhanden.';
            confidenceByTask[taskDef.taskKey] = null;
            isApprovedByTask[taskDef.taskKey] = false;
            continue;
          }
          const relatedExamTask =
            exam?.tasks?.find(t => String((t as any)?.taskNumber ?? '') === taskDef.taskKey) ??
            exam?.tasks?.[Number(taskDef.taskKey) - 1];

          const resolveExamChildForSubmissionChild = (
            examParent: any,
            submissionChild: any,
            childIndex: number,
          ): any => {
            const examChildren = Array.isArray(examParent?.subTasks) ? examParent.subTasks : [];
            const byTaskNumber = examChildren.find(
              (examChild: any) => String(examChild?.taskNumber ?? '') === String(submissionChild?.taskNumber ?? ''),
            );
            return byTaskNumber ?? examChildren[childIndex];
          };

          const aggregateTaskTree = (
            submissionNode: any,
            examNode: any,
            pathLabel: string,
            qualifiedPath: string[],
          ): {
            points: number;
            ownLanguageUseEarned: number;
            unresolvedConfidence: number | null;
            hasOpenClarification: boolean;
            ownLanguageUseUnresolvedConfidence: number | null;
            ownLanguageUseHasOpenClarification: boolean;
            explanations: string[];
          } => {
            const resolvedSubmissionNode = unwrapDoc<any>(submissionNode) ?? submissionNode;
            const resolvedExamNode = unwrapDoc<any>(examNode) ?? examNode;
            const nodeNeedsAnswer = resolvedExamNode?.needsAnswer !== false;
            const nodeMaxPoints = ownMaxPoints(resolvedExamNode);
            const nodeGradingResult = unwrapDoc<any>(resolvedSubmissionNode?.gradingResult);
            const nodeBoundingBoxes = Array.isArray(resolvedSubmissionNode?.boundingBoxes)
              ? (resolvedSubmissionNode.boundingBoxes as any[])
              : undefined;

            const ownPoints = nodeNeedsAnswer
              ? resolveAchievedPoints(nodeGradingResult, Math.max(0, nodeMaxPoints))
              : 0;

            // Solo language-use rule is tracked separately from the node's own content points and
            // surfaced in its own "· Sprachliche Leistung" column, mirroring how group points already
            // get their own columns (Part C2) rather than being folded into one combined number. Its
            // result lives in the shared Submission.languageUseRuleResults now, same as a multi-member
            // group's.
            const soloLanguageUseRule = nodeNeedsAnswer
              ? getSoloLanguageUseRule(qualifiedPath.join('|'), languageUseSchemeRules)
              : null;
            const ownLanguageUsePointsResult = nodeNeedsAnswer
              ? resolveSoloLanguageUsePoints(qualifiedPath.join('|'), languageUseSchemeRules, sub.languageUseRuleResults)
              : null;
            const ownLanguageUsePoints = ownLanguageUsePointsResult?.earnedPoints ?? 0;
            const ownLanguageUseRuleResult = soloLanguageUseRule
              ? (sub.languageUseRuleResults ?? []).find(
                  r => String(r?.languageUseSchemeId ?? '') === soloLanguageUseRule.schemeId,
                )
              : undefined;

            const ownConfidence = nodeNeedsAnswer
              ? resolveSummaryUnresolvedConfidence(nodeGradingResult, nodeBoundingBoxes)
              : null;

            const ownApproved = nodeNeedsAnswer ? nodeGradingResult?.isApproved === true : true;
            const ownRequiresClarification =
              ownConfidence != null && ownConfidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE && !ownApproved;

            // Same "unresolved confidence" logic as content, applied to this node's own languageUseCriteria
            // (only meaningful when ownLanguageUsePointsResult is non-null, i.e. the node actually has one).
            const ownLanguageUseConfidence =
              ownLanguageUsePointsResult != null ? parseNumber(ownLanguageUseRuleResult?.confidence) : null;
            const ownLanguageUseApproved = ownLanguageUseRuleResult?.isApproved === true;
            const ownLanguageUseRequiresClarification =
              ownLanguageUsePointsResult != null &&
              ownLanguageUseConfidence != null &&
              ownLanguageUseConfidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE &&
              !ownLanguageUseApproved;

            const ownExplanation = normalizeExplanation(nodeGradingResult?.explanation);
            const ownExplanationLabel = ownExplanation ? `${pathLabel}: ${ownExplanation}` : null;

            const submissionChildren = Array.isArray(resolvedSubmissionNode?.subTasksSubmission)
              ? resolvedSubmissionNode.subTasksSubmission
              : [];

            const childAggregates = submissionChildren.map((child: any, childIndex: number) => {
              const examChild = resolveExamChildForSubmissionChild(resolvedExamNode, child, childIndex);
              const childLabelPart = String(child?.taskNumber ?? childIndex + 1);
              return aggregateTaskTree(child, examChild, `${pathLabel}.${childLabelPart}`, [
                ...qualifiedPath,
                childLabelPart,
              ]);
            });

            const childPoints = childAggregates.reduce((sum, item) => sum + item.points, 0);
            const childOwnLanguageUseEarned = childAggregates.reduce(
              (sum, item) => sum + item.ownLanguageUseEarned,
              0,
            );
            const childUnresolvedConfidences = childAggregates
              .map(item => item.unresolvedConfidence)
              .filter((value): value is number => value != null && Number.isFinite(value));

            const unresolvedConfidenceValues = [
              ownRequiresClarification ? ownConfidence : null,
              ...childUnresolvedConfidences,
            ].filter((value): value is number => value != null && Number.isFinite(value));

            const minUnresolvedConfidence =
              unresolvedConfidenceValues.length > 0 ? Math.min(...unresolvedConfidenceValues) : null;
            const hasOpenClarification =
              ownRequiresClarification || childAggregates.some(item => item.hasOpenClarification);

            const childOwnLanguageUseUnresolvedConfidences = childAggregates
              .map(item => item.ownLanguageUseUnresolvedConfidence)
              .filter((value): value is number => value != null && Number.isFinite(value));

            const ownLanguageUseUnresolvedConfidenceValues = [
              ownLanguageUseRequiresClarification ? ownLanguageUseConfidence : null,
              ...childOwnLanguageUseUnresolvedConfidences,
            ].filter((value): value is number => value != null && Number.isFinite(value));

            const minOwnLanguageUseUnresolvedConfidence =
              ownLanguageUseUnresolvedConfidenceValues.length > 0
                ? Math.min(...ownLanguageUseUnresolvedConfidenceValues)
                : null;
            const ownLanguageUseHasOpenClarification =
              ownLanguageUseRequiresClarification ||
              childAggregates.some(item => item.ownLanguageUseHasOpenClarification);

            const explanations = [
              ...(ownExplanationLabel ? [ownExplanationLabel] : []),
              ...childAggregates.flatMap(item => item.explanations),
            ];

            return {
              points: ownPoints + childPoints,
              ownLanguageUseEarned: ownLanguageUsePoints + childOwnLanguageUseEarned,
              unresolvedConfidence: minUnresolvedConfidence,
              hasOpenClarification,
              ownLanguageUseUnresolvedConfidence: minOwnLanguageUseUnresolvedConfidence,
              ownLanguageUseHasOpenClarification,
              explanations,
            };
          };

          const rootLabel = `Teilaufgabe ${String(mainTask?.taskNumber ?? taskDef.taskKey)}`;
          const aggregated = aggregateTaskTree(mainTask, relatedExamTask, rootLabel, [taskDef.taskKey]);

          gradingByTask[taskDef.taskKey] = aggregated.explanations.join(' | ') || 'Noch kein Grading vorhanden.';
          pointsByTask[taskDef.taskKey] = Math.max(0, Math.min(aggregated.points, taskDef.maxPoints));
          ownLanguageUsePointsByTask[taskDef.taskKey] = Math.max(
            0,
            Math.min(aggregated.ownLanguageUseEarned, taskDef.languageUseMaxPoints),
          );
          confidenceByTask[taskDef.taskKey] = aggregated.unresolvedConfidence;
          isApprovedByTask[taskDef.taskKey] = !aggregated.hasOpenClarification;
          ownLanguageUseConfidenceByTask[taskDef.taskKey] = aggregated.ownLanguageUseUnresolvedConfidence;
          ownLanguageUseIsApprovedByTask[taskDef.taskKey] = !aggregated.ownLanguageUseHasOpenClarification;
        }

        // Part C2/C3: this submission's shared per-group language-use results, matched by
        // languageUseSchemeId, routed through the same resolveLanguageUseGroupPoints primitive the
        // server uses.
        const languageUseGroupPointsBySchemeId: Record<string, number> = {};
        const languageUseGroupConfidenceBySchemeId: Record<string, number | null> = {};
        const languageUseGroupIsApprovedBySchemeId: Record<string, boolean> = {};
        let languageUseGroupsEarnedPoints = 0;
        for (const col of languageUseGroupColumns) {
          const matchingResult = (sub.languageUseRuleResults ?? []).find(
            r => String(r?.languageUseSchemeId ?? '') === col.schemeId,
          );
          const scheme = languageUseSchemeRules.find((s: any) => String(s?._id ?? '') === col.schemeId);
          const { earnedPoints } = resolveLanguageUseGroupPoints(
            scheme ?? { maxPoints: col.maxPoints, dependsOn: col.dependsOn, criteria: '' },
            matchingResult,
          );
          languageUseGroupPointsBySchemeId[col.schemeId] = earnedPoints;
          languageUseGroupsEarnedPoints += earnedPoints;
          languageUseGroupConfidenceBySchemeId[col.schemeId] = matchingResult
            ? (parseNumber(matchingResult.confidence) ?? null)
            : null;
          languageUseGroupIsApprovedBySchemeId[col.schemeId] = matchingResult?.isApproved === true;
        }

        const totalP =
          activeTaskDefinitions.reduce(
            (s, t) => s + (pointsByTask[t.taskKey] ?? 0) + (ownLanguageUsePointsByTask[t.taskKey] ?? 0),
            0,
          ) + languageUseGroupsEarnedPoints;
        const totalMax =
          activeTaskDefinitions.reduce((s, t) => s + t.maxPoints + t.languageUseMaxPoints, 0) +
          languageUseGroupsMaxPoints;
        const perc = totalMax > 0 ? (totalP / totalMax) * 100 : 0;

        // Grammar/spelling error detection runs for every grammar-eligible task regardless of FI vs.
        // rules mode, so `grammarByTask` (feeding the reworked Language Use tab matrix) collects errors
        // across ALL grammar-eligible nodes. `fiGrammarByTask`/the FI aggregate below stay scoped to
        // still-FI-mode nodes only, since those feed the Fehlerindex grade-step deduction and the
        // legacy summary column, which must never count rules-graded content.
        let grammarErrors = 0;
        let grammarWords = 0;
        let hasGrammar = false;
        let firstGrammarTaskKey: string | null = null;
        const grammarByTask: StudentSummary['grammarByTask'] = {};
        const fiGrammarByTask: StudentSummary['fiGrammarByTask'] = {};
        const collectGrammar = (
          node: any,
          parentPath: string[],
          accAll: { errors: number; words: number; found: boolean },
          accFi: { errors: number; words: number; found: boolean },
        ) => {
          const resolved = unwrapDoc<any>(node) ?? node;
          const nodeTaskNumber = String(resolved?.taskNumber ?? '');
          const path = nodeTaskNumber ? [...parentPath, nodeTaskNumber] : parentPath;
          // Same "|"-joined qualified path taskGrammarModeByTaskKey is keyed by — a bare taskNumber
          // would be ambiguous whenever it's reused as a subtask label under more than one parent.
          const nodeMode = taskGrammarModeByTaskKey[path.join('|')];
          // FI applies to every grammar-eligible task (rule-covered or not) once the switch is on —
          // rule coverage no longer exempts a task from the aggregate FI deduction.
          const isFiNode = forceFi && (nodeMode == null ? true : nodeMode !== 'none');
          const grammar = unwrapDoc<any>(resolved?.grammarEvaluation);
          if (grammar) {
            accAll.found = true;
            accAll.errors += parseNumber(grammar.errorCount) ?? 0;
            accAll.words += parseNumber(grammar.wordCount) ?? 0;
            if (isFiNode) {
              accFi.found = true;
              accFi.errors += parseNumber(grammar.errorCount) ?? 0;
              accFi.words += parseNumber(grammar.wordCount) ?? 0;
            }
          }
          const children = Array.isArray(resolved?.subTasksSubmission) ? resolved.subTasksSubmission : [];
          for (const child of children) collectGrammar(child, path, accAll, accFi);
        };
        for (const [taskIdx, mainTask] of (sub.taskSubmissions || []).entries()) {
          const taskKey = String((mainTask as any).taskNumber ?? taskIdx + 1);
          const accAll = { errors: 0, words: 0, found: false };
          const accFi = { errors: 0, words: 0, found: false };
          collectGrammar(mainTask, [], accAll, accFi);
          grammarByTask[taskKey] = accAll.found
            ? {
                errorCount: accAll.errors,
                wordCount: accAll.words,
                fehlerindex: accAll.words > 0 ? (accAll.errors / accAll.words) * 100 : 0,
              }
            : null;
          fiGrammarByTask[taskKey] = accFi.found
            ? {
                errorCount: accFi.errors,
                wordCount: accFi.words,
                fehlerindex: accFi.words > 0 ? (accFi.errors / accFi.words) * 100 : 0,
              }
            : null;
          if (accFi.found) {
            hasGrammar = true;
            grammarErrors += accFi.errors;
            grammarWords += accFi.words;
            if (firstGrammarTaskKey === null) firstGrammarTaskKey = taskKey;
          }
        }

        // Fehlerindex grade-step deduction on the overall grade — applies whenever the switch is on,
        // independent of whether any of the aggregated tasks also have a configured grammar rule.
        const aggregateFi = hasGrammar && grammarWords > 0 ? (grammarErrors / grammarWords) * 100 : null;
        const fiSteps = forceFi && aggregateFi !== null ? fiDeductionSteps(aggregateFi, grammarFiSettings) : 0;
        const baseGrade = gradeFromPercentage(perc, gradingScale);

        // Hypothetical "grade with FI" (Language Use tab): what the grade would be if FI were applied
        // to this exam right now — i.e. every grammar-eligible task (not just currently-FI ones), since
        // flipping the FI switch reclassifies all of them. Shown regardless of the switch's state.
        const hypotheticalWords = Object.values(grammarByTask).reduce((sum, g) => sum + (g?.wordCount ?? 0), 0);
        const hypotheticalErrors = Object.values(grammarByTask).reduce((sum, g) => sum + (g?.errorCount ?? 0), 0);
        const hypotheticalFi = hypotheticalWords > 0 ? (hypotheticalErrors / hypotheticalWords) * 100 : null;
        const hypotheticalFiSteps = hypotheticalFi !== null ? fiDeductionSteps(hypotheticalFi, grammarFiSettings) : 0;
        const gradeWithFiApplied = applyGradeStepDeduction(baseGrade, hypotheticalFiSteps, gradingScale);

        return {
          id: sub._id,
          submissionIndex: idx,
          name: resolveStudentName(sub.studentName, `Schüler ${idx + 1}`),
          pointsByTask,
          ownLanguageUsePointsByTask,
          ownLanguageUseConfidenceByTask,
          ownLanguageUseIsApprovedByTask,
          gradingByTask,
          confidenceByTask,
          isApprovedByTask,
          totalPoints: totalP,
          totalMaxPoints: totalMax,
          percentage: perc,
          grade: applyGradeStepDeduction(baseGrade, fiSteps, gradingScale),
          baseGrade,
          fiDeductionSteps: fiSteps,
          gradeWithFiApplied,
          grammarByTask,
          fiGrammarByTask,
          grammarErrorCount: hasGrammar ? grammarErrors : null,
          grammarFehlerindex: aggregateFi,
          firstGrammarTaskKey,
          languageUseGroupPointsBySchemeId,
          languageUseGroupConfidenceBySchemeId,
          languageUseGroupIsApprovedBySchemeId,
        };
      }),
    [
      submissions,
      activeTaskDefinitions,
      exam,
      gradingScale,
      forceFi,
      grammarFiSettings,
      languageUseGroupColumns,
      languageUseSchemeRules,
      taskGrammarModeByTaskKey,
    ],
  );

  const gradingStatusByTask = useMemo(
    () =>
      activeTaskDefinitions.reduce<Record<string, TaskStatusSummary>>((acc, tDef) => {
        let t = 0;
        let f = 0;
        let p = 0;
        let pe = 0;
        let e = 0;
        for (const sub of submissions) {
          const mainT =
            (sub.taskSubmissions || []).find(x => String(x.taskNumber ?? '') === tDef.taskKey) ??
            (sub.taskSubmissions || [])[Number(tDef.taskKey) - 1];
          if (!mainT) continue;
          t++;
          const uw = unwrapDoc(mainT) as any;
          let state = typeof uw.gradingState === 'string' ? uw.gradingState : 'PENDING';
          const subT = uw.subTasksSubmission || [];
          if (subT.length > 0) {
            if (subT.some((st: any) => unwrapDoc(st).gradingState === 'ERROR')) state = 'ERROR';
            else if (subT.every((st: any) => isTaskFinishedByState(unwrapDoc(st).gradingState))) state = 'COMPLETED';
          }
          if (state === 'ERROR') {
            e++;
            f++;
          } else if (isTaskFinishedByState(state)) {
            f++;
          } else if (state === 'PROCESSING') {
            p++;
          } else {
            pe++;
          }
        }
        acc[tDef.taskKey] = {
          total: t,
          finished: f,
          processing: p,
          pending: pe,
          error: e,
          isComplete: t > 0 && f >= t,
        };
        return acc;
      }, {}),
    [activeTaskDefinitions, submissions],
  );

  const gradeDistribution = gradingScale.borders.map(b => ({
    grade: b.label,
    count: studentSummaries.filter(s => s.grade === b.label).length,
  }));
  const averageGrade =
    studentSummaries.length > 0
      ? (() => {
          const avg = averageNumericGrade(
            studentSummaries.map(s => s.grade),
            gradingScale,
          );
          return avg != null ? roundToOne(avg) : null;
        })()
      : null;
  const averagePercentage =
    studentSummaries.length > 0
      ? roundToOne(studentSummaries.reduce((s, st) => s + st.percentage, 0) / studentSummaries.length)
      : 0;
  const averageTotalPoints =
    studentSummaries.length > 0
      ? roundToOne(studentSummaries.reduce((s, st) => s + st.totalPoints, 0) / studentSummaries.length)
      : 0;

  const filteredStudentSummaries = useMemo(() => {
    const q = studentQuery.trim().toLowerCase();
    if (!q) return studentSummaries;
    return studentSummaries.filter(s => s.name.toLowerCase().includes(q));
  }, [studentQuery, studentSummaries]);

  const clarificationCases = useMemo<ClarificationCase[]>(() => {
    const buildTaskLabel = (path: string[]): string => path.filter(Boolean).join(' - ');

    const findExamTaskByPath = (path: string[]): any | undefined => {
      if (!exam?.tasks || path.length === 0) return undefined;
      let list: any[] = exam.tasks as any[];
      let node: any | undefined;
      for (const seg of path) {
        node = list.find((t: any) => String(t?.taskNumber ?? '').trim() === seg);
        if (!node) return undefined;
        list = Array.isArray(node?.subTasks) ? node.subTasks : [];
      }
      return node;
    };

    // Picks whichever language-use source (this task's own merged languageUseCriteria, and/or any
    // linked LanguageUseScheme group) is actually the worse one — a task can carry both at once.
    const resolveWorstLanguageUseSource = (
      nodeTaskNumber: string,
      languageUseRuleResultsForSubmission: any[] | undefined,
    ): { confidence: number; points: number; maxPoints: number; reason: string } | null => {
      const candidates: Array<{ confidence: number; points: number; maxPoints: number; reason: string }> = [];

      for (const result of findLanguageUseRuleResultsForTask(
        nodeTaskNumber,
        languageUseSchemeRules,
        languageUseRuleResultsForSubmission,
      )) {
        if (result.isApproved === true) continue;
        const conf = parseNumber(result.confidence);
        if (conf == null) continue;
        const scheme = languageUseSchemeRules.find((s: any) => String(s?._id ?? '') === String(result.languageUseSchemeId));
        if (!scheme) continue;
        const groupPoints = resolveLanguageUseGroupPoints(scheme, result);
        candidates.push({
          confidence: conf,
          points: groupPoints.earnedPoints,
          maxPoints: groupPoints.maxPoints,
          reason: normalizeExplanation(result.explanation),
        });
      }

      if (candidates.length === 0) return null;
      return candidates.reduce((worst, c) => (c.confidence < worst.confidence ? c : worst));
    };

    // Merge content + language-use uncertainties into one entry per (submission, task) via the central
    // findUncertainties/findLanguageUseUncertainties detectors — a task uncertain in both dimensions
    // must only produce one clarification case, not two.
    const keyOf = (u: { submissionIdx: number; mainTaskIdx: number; subTaskIdx: number | null }) =>
      `${u.submissionIdx}::${u.mainTaskIdx}::${u.subTaskIdx}`;
    const combined = new Map<
      string,
      {
        submissionIdx: number;
        mainTaskIdx: number;
        subTaskIdx: number | null;
        hasContent: boolean;
        hasLanguageUse: boolean;
      }
    >();
    for (const u of findUncertainties(submissions as any)) {
      combined.set(keyOf(u), { ...u, hasContent: true, hasLanguageUse: false });
    }
    for (const u of findLanguageUseUncertainties(submissions as any, languageUseSchemeRules)) {
      const key = keyOf(u);
      const existing = combined.get(key);
      if (existing) existing.hasLanguageUse = true;
      else combined.set(key, { ...u, hasContent: false, hasLanguageUse: true });
    }

    const results: ClarificationCase[] = [];

    for (const entry of combined.values()) {
      const submission = submissions[entry.submissionIdx];
      const mainTask = submission?.taskSubmissions?.[entry.mainTaskIdx];
      if (!mainTask) continue;
      const mainTaskNumber = String((mainTask as any)?.taskNumber ?? '').trim();
      if (!mainTaskNumber) continue;

      let submissionNode: any = mainTask;
      let path = [mainTaskNumber];
      let depth = 0;
      if (entry.subTaskIdx !== null) {
        const flatEntry = flattenSubTasksWithMeta(mainTask as any).find(e => e.flatIndex === entry.subTaskIdx);
        if (!flatEntry) continue;
        submissionNode = flatEntry.task;
        path = [mainTaskNumber, ...flatEntry.numberPath];
        depth = flatEntry.depth;
      }

      const examNode = findExamTaskByPath(path);
      const nodeNeedsAnswer = examNode?.needsAnswer !== false && submissionNode?.needsAnswer !== false;
      if (!nodeNeedsAnswer) continue;

      const gradingResult = unwrapDoc<any>(submissionNode?.gradingResult);
      const studentName = resolveStudentName(submission?.studentName, `Schüler ${entry.submissionIdx + 1}`);
      const base = {
        id: `${entry.submissionIdx}:${path.join('|')}`,
        submissionIndex: entry.submissionIdx,
        submissionId: submission?._id ?? '',
        studentName,
        taskKey: path.join('.'),
        taskLabel: buildTaskLabel(path),
        taskPath: path,
        depth,
        mainTaskId: (mainTask as any)?._id ?? null,
        subTaskId: depth === 0 ? null : (submissionNode?._id ?? null),
      };

      if (entry.hasContent) {
        const confidenceValue = resolveSummaryUnresolvedConfidence(
          gradingResult,
          Array.isArray(submissionNode?.boundingBoxes) ? (submissionNode.boundingBoxes as any[]) : undefined,
        );
        const nodeMaxPoints = ownMaxPoints(examNode);
        results.push({
          ...base,
          confidence: confidenceValue ?? 1,
          points: resolveAchievedPoints(gradingResult, Math.max(0, nodeMaxPoints)),
          maxPoints: Math.max(0, nodeMaxPoints),
          reason: normalizeExplanation(gradingResult?.explanation) || 'Unsichere Bewertung ohne Details.',
          boxMode: 'content',
        });
        continue;
      }

      // The "|"-joined full ancestor path (already built above for taskKey/id) — the same qualified
      // reference LanguageUseScheme.dependsOn now stores, not just this node's bare (ambiguous) label.
      const qualifiedTaskNumber = path.join('|');
      const worst = resolveWorstLanguageUseSource(qualifiedTaskNumber, submission?.languageUseRuleResults);
      if (!worst) continue;
      results.push({
        ...base,
        confidence: worst.confidence,
        points: worst.points,
        maxPoints: worst.maxPoints,
        reason: worst.reason || 'Unsichere Bewertung ohne Details.',
        boxMode: 'languageUse',
      });
    }

    return results;
  }, [exam, submissions, languageUseSchemeRules]);

  const sortedClarificationCases = useMemo(() => {
    const sorted = [...clarificationCases].sort((a, b) => {
      if (sortField === 'student') return a.studentName.localeCompare(b.studentName, 'de', { sensitivity: 'base' });
      if (sortField === 'task') return a.taskLabel.localeCompare(b.taskLabel, 'de', { sensitivity: 'base' });
      return 1 - a.confidence - (1 - b.confidence);
    });
    if (sortDirection === 'desc') sorted.reverse();
    return sorted;
  }, [clarificationCases, sortDirection, sortField]);

  // Mirrors the Schülerübersicht's column structure exactly: one row per content task, one per
  // own-criteria language-use task (gated on hasOwnLanguageUseCriteria, not languageUseMaxPoints > 0
  // — the latter can be 0 even when a rule genuinely exists), and one per language-use-group column —
  // instead of content tasks only. `kind`/`label` let the component apply the same "· Sprachliche
  // Leistung" i18n suffix StudentsOverviewTab uses for own-language-use columns (group labels are
  // already fully formed).
  const taskAverages = useMemo(() => {
    const average = (pick: (s: StudentSummary) => number): number =>
      studentSummaries.length > 0 ? studentSummaries.reduce((sum, s) => sum + pick(s), 0) / studentSummaries.length : 0;

    const rows: Array<{
      key: string;
      kind: 'content' | 'ownLanguageUse' | 'group';
      taskKey: string;
      label: string;
      avgPoints: number;
      maxPoints: number;
      avgPercentage: number;
    }> = [];

    for (const task of activeTaskDefinitions) {
      const avgPoints = average(s => s.pointsByTask[task.taskKey] ?? 0);
      rows.push({
        key: `content:${task.taskKey}`,
        kind: 'content',
        taskKey: task.taskKey,
        label: task.label,
        avgPoints,
        maxPoints: task.maxPoints,
        avgPercentage: task.maxPoints > 0 ? (avgPoints / task.maxPoints) * 100 : 0,
      });

      if (task.hasOwnLanguageUseCriteria) {
        const avgLanguageUsePoints = average(s => s.ownLanguageUsePointsByTask[task.taskKey] ?? 0);
        rows.push({
          key: `ownLanguageUse:${task.taskKey}`,
          kind: 'ownLanguageUse',
          taskKey: task.taskKey,
          label: task.label,
          avgPoints: avgLanguageUsePoints,
          maxPoints: task.languageUseMaxPoints,
          avgPercentage: task.languageUseMaxPoints > 0 ? (avgLanguageUsePoints / task.languageUseMaxPoints) * 100 : 0,
        });
      }
    }

    for (const col of languageUseGroupColumns) {
      const avgGroupPoints = average(s => s.languageUseGroupPointsBySchemeId[col.schemeId] ?? 0);
      rows.push({
        key: `group:${col.schemeId}`,
        kind: 'group',
        taskKey: col.schemeId,
        label: col.label,
        avgPoints: avgGroupPoints,
        maxPoints: col.maxPoints,
        avgPercentage: col.maxPoints > 0 ? (avgGroupPoints / col.maxPoints) * 100 : 0,
      });
    }

    return rows;
  }, [activeTaskDefinitions, languageUseGroupColumns, studentSummaries]);

  const worstTask = useMemo(
    () =>
      taskAverages.length === 0 ? null : taskAverages.reduce((w, c) => (c.avgPercentage < w.avgPercentage ? c : w)),
    [taskAverages],
  );

  // Tasks shown in the language-use-tab matrix: grammar task types by exam definition, plus any task
  // a student actually has a grammar evaluation for (fallback for older exams without taskType).
  // A combined main task can itself be a purely structural type (e.g. TASK_FRAME) while its subtasks
  // carry the real essay/poetry/etc type, so eligibility must be checked at every depth.
  const hasGrammarTaskTypeRecursive = (task: any): boolean =>
    GRAMMAR_TASK_TYPES.has(String(task?.taskType ?? '')) ||
    (Array.isArray(task?.subTasks) ? task.subTasks : []).some(hasGrammarTaskTypeRecursive);

  // Part C4: mixed-mode exams can have some tasks still FI-graded and others rules-graded — the FI
  // matrix only makes sense for the former, since the latter's language use is graded (and surfaced)
  // via the rules-based own-criteria/group columns instead.
  const hasFiModeRecursive = (task: any): boolean =>
    taskGrammarModeByTaskKey[String(task?.taskNumber ?? '')] === 'fi' ||
    (Array.isArray(task?.subTasks) ? task.subTasks : []).some(hasFiModeRecursive);

  const grammarTaskDefinitions = useMemo(
    () =>
      activeTaskDefinitions.filter(taskDef => {
        const examTask =
          exam?.tasks?.find(t => String((t as any)?.taskNumber ?? '') === taskDef.taskKey) ??
          exam?.tasks?.[Number(taskDef.taskKey) - 1];
        if (hasGrammarTaskTypeRecursive(examTask) && hasFiModeRecursive(examTask)) return true;
        return studentSummaries.some(s => s.fiGrammarByTask[taskDef.taskKey]);
      }),
    [activeTaskDefinitions, exam, studentSummaries, taskGrammarModeByTaskKey],
  );

  // Reworked Language Use tab: which activeTaskDefinitions columns are grammar-eligible at all (FI OR
  // rules mode), so the matrix can show every task (like Schülerübersicht) and mark ineligible ones
  // with "–" instead of a count. Deliberately broader than grammarTaskDefinitions above.
  const grammarEligibleTaskKeys = useMemo(
    () =>
      new Set(
        activeTaskDefinitions
          .filter(taskDef => {
            const examTask =
              exam?.tasks?.find(t => String((t as any)?.taskNumber ?? '') === taskDef.taskKey) ??
              exam?.tasks?.[Number(taskDef.taskKey) - 1];
            if (hasGrammarTaskTypeRecursive(examTask)) return true;
            return studentSummaries.some(s => s.grammarByTask[taskDef.taskKey]);
          })
          .map(taskDef => taskDef.taskKey),
      ),
    [activeTaskDefinitions, exam, studentSummaries],
  );

  // Compact list for the new "which tasks have language-use rules" message: every solo (1-member)
  // LanguageUseScheme, labeled the same way task columns are (dotted for nested subtasks).
  // Multi-member rules are listed separately via languageUseGroupColumns.
  const ownLanguageUseRuleEntries = useMemo(
    () =>
      languageUseSchemeRules
        .filter((scheme: any) => Array.isArray(scheme?.dependsOn) && scheme.dependsOn.length === 1)
        .map((scheme: any) => ({
          taskKey: String(scheme._id ?? scheme.dependsOn[0]),
          label: formatTaskReferenceForDisplay(String(scheme.dependsOn[0])),
          maxPoints: Number(scheme.maxPoints) || 0,
        })),
    [languageUseSchemeRules],
  );

  const frequentErrorSources = useMemo(() => {
    const patterns = [
      { label: 'Vorzeichenfehler', regex: /vorzeichen/i },
      { label: 'Rechenfehler', regex: /rechenfehler|gerechnet|berechnung/i },
      { label: 'Einheitenproblem', regex: /einheit|einheiten/i },
      { label: 'Begründung unvollständig', regex: /begründung|argumentation|nachvollziehbar/i },
      { label: 'Formelfehler', regex: /formel|ansatz/i },
      { label: 'Übertragungsfehler', regex: /übertrag|abschreib|abgeschrieben/i },
    ];
    const texts = studentSummaries.flatMap(s => Object.values(s.gradingByTask));
    return patterns
      .map(p => ({ label: p.label, count: texts.reduce((sum, text) => (p.regex.test(text) ? sum + 1 : sum), 0) }))
      .filter(i => i.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  }, [studentSummaries]);

  // --- ACTIONS ---
  const handleClarificationDecision = async (item: ClarificationCase, value: 'approve' | 'reject') => {
    if (value !== 'approve') {
      setClarificationDecisions(p => ({ ...p, [item.id]: value }));
      return;
    }

    if (!onSaveUpdate || !item.mainTaskId) {
      setClarificationDecisions(p => ({ ...p, [item.id]: value }));
      return;
    }

    setClarificationSaving(prev => ({ ...prev, [item.id]: true }));
    try {
      await onSaveUpdate(item.submissionId, item.mainTaskId, item.subTaskId, null, { isApproved: true });
      setClarificationDecisions(p => ({ ...p, [item.id]: value }));
    } catch (error) {
      logger.error({ err: error, context: 'handleClarificationDecision' }, 'Failed to persist approval');
    } finally {
      setClarificationSaving(prev => ({ ...prev, [item.id]: false }));
    }
  };

  const handleNavigateToClarificationTask = (item: ClarificationCase) => {
    onNavigateToTask?.(item.submissionIndex, item.taskKey, 'correction', item.boxMode);
  };

  const updateGradingScale = (scale: GradingScale) => {
    setGradingScale(scale);
    pendingScaleRef.current = scale;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (pendingScaleRef.current && props.onSaveGradingScale) {
        props
          .onSaveGradingScale(pendingScaleRef.current)
          .catch(err => logger.error({ err, context: 'updateGradingScale' }, 'Failed to save grading scale'));
        pendingScaleRef.current = null;
      }
    }, 800);
  };

  const updateGrammarFiSettings = (settings: GrammarFiSettings) => {
    setGrammarFiSettings(settings);
    pendingFiSettingsRef.current = settings;
    if (fiDebounceTimerRef.current) clearTimeout(fiDebounceTimerRef.current);
    fiDebounceTimerRef.current = setTimeout(() => {
      if (pendingFiSettingsRef.current && props.onSaveGrammarFiSettings) {
        props
          .onSaveGrammarFiSettings(pendingFiSettingsRef.current)
          .catch(err =>
            logger.error({ err, context: 'updateGrammarFiSettings' }, 'Failed to save grammar FI settings'),
          );
        pendingFiSettingsRef.current = null;
      }
    }, 800);
  };

  return {
    refs: dom.refs,
    state: {
      activeTab,
      studentQuery,
      clarificationDecisions,
      clarificationSaving,
      isCurveEnabled,
      curveStrength,
      gradingScale,
      grammarFiSettings,
      sortField,
      sortDirection,
    },
    computed: {
      ...dom.computed,
      role,
      submissions,
      activeTaskDefinitions,
      grammarTaskDefinitions,
      hasAnyGrammarEligibleTask,
      grammarEligibleTaskKeys,
      ownLanguageUseRuleEntries,
      languageUseGroupColumns,
      taskGrammarModeByTaskKey,
      resolvedAlwaysUseFi: forceFi,
      totalPossiblePoints,
      studentSummaries,
      gradeDistribution,
      gradingStatusByTask,
      averageGrade,
      averagePercentage,
      averageTotalPoints,
      gradeCardClasses: getGradeCardClasses(averagePercentage),
      pointsCardClasses: getPointsCardClasses(averagePercentage),
      filteredStudentSummaries,
      sortedClarificationCases,
      unresolvedCount: clarificationCases.filter(i => clarificationDecisions[i.id] !== 'approve').length,
      taskAverages,
      worstTask,
      frequentErrorSources,
      isPdfDownloadDisabled: isDownloadingReports || studentSummaries.length === 0 || !canDownloadReports,
      isCsvDownloadDisabled:
        !onDownloadGradebookCsv ||
        isDownloadingGradebookCsv ||
        studentSummaries.length === 0 ||
        !canDownloadGradebookCsv,
    },
    actions: {
      setActiveTab,
      setStudentQuery,
      setIsCurveEnabled,
      setCurveStrength,
      setSortField,
      setSortDirection,
      handleClarificationDecision,
      handleNavigateToClarificationTask,
      updateGradingScale,
      updateGrammarFiSettings,
      onNavigateToTask,
      onDownloadReports,
      onDownloadGradebookCsv,
    },
    props, // pass through for UI components
  };
}

export type SummaryStateStore = ReturnType<typeof useSummaryState>;
